/**
 * Provides APIs to manage view metadata in rule store
 * @module ViewResources
 */
import Constants from '../constants';
import * as StreamObservable from '../observables/stream-observable';
import ContextProcessor from '../context/context-processor';
import { C11nEnv } from '../interpreter/c11n-env';
import type { DxAPIResponseObject } from '../case/types';

import Utils from './utils';
import { getTranslatedDXAPIResponse } from './dxapi-response-translator';
import loadComponents from './component-loader';

/**
 * This is used to update rule store with view metadata and loads respective components
 * @function
 * @example <caption> Example for updateViewResources</caption>
 * PCore.updateViewResources(dxAPIResponse)
 *
 * @param dxAPIResponse - An object containing view metadata.
 */
export const updateViewResources = async (dxAPIResponse: DxAPIResponseObject) => {
  const translatedDXAPIResponse = getTranslatedDXAPIResponse(dxAPIResponse);
  const components = Utils.getIn(translatedDXAPIResponse, Constants.COMPONENTS);
  StreamObservable.publishResponse(translatedDXAPIResponse, {}, true);
  await loadComponents(components);
};

/**
 * This is used to fetch view metadata from rule store
 * @function
 * @example <caption> Example for fetchViewResources</caption>
 * const viewMetaData = PCore.fetchViewResources("viewname", getPConnect(), "OPB1HW-MyApp-Work-MyCase");
 *
 * @param viewName - Name of the view rule.
 * @param c11nEnv  - Context object. For example: getPConnect()
 * @param classID  - Class name of the case type
 * @returns An object containing view metadata
 */
export const fetchViewResources = (
  viewName: string,
  c11nEnv: C11nEnv,
  classID: string
): {
  type: string;
  config?: Record<string, unknown>;
} => {
  return ContextProcessor.resolveView(viewName, c11nEnv, classID);
};
